/**
 * /:projectCode/tasks/:shorthand — full-page card view for a task.
 *
 * Legacy parity URL shape (3mote-fe `src/router.jsx`:127). The route
 * resolves projectCode → projectId via the existing dashboard cache,
 * then renders the same content as `<CardDetailModal>` inline so the
 * AppShell sidebar stays visible alongside.
 */

import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect } from 'react'

import { getAccessToken } from '@/auth/cookie-helpers'
import { CardDetailPage, type CardPageSearch } from '@/features/board/components/CardDetailPage'

export const Route = createFileRoute('/$projectCode/tasks/$shorthand')({
  component: TaskDeepLinkRoute,
  validateSearch: (search: Record<string, unknown>): CardPageSearch => ({
    commentId: typeof search.commentId === 'string' ? search.commentId : undefined,
    parentCommentId:
      typeof search.parentCommentId === 'string' ? search.parentCommentId : undefined,
  }),
})

function TaskDeepLinkRoute() {
  const navigate = Route.useNavigate()
  const { commentId, parentCommentId } = Route.useSearch()
  const stripCommentId = useCallback(
    () =>
      navigate({
        search: (prev) => ({ ...prev, commentId: undefined, parentCommentId: undefined }),
        replace: true,
      }),
    [navigate],
  )
  const { projectCode, shorthand } = Route.useParams()

  useEffect(() => {
    if (!getAccessToken()) {
      navigate({ to: '/login' })
    }
  }, [navigate])

  if (!getAccessToken()) return null

  return (
    <CardDetailPage
      kind="task"
      projectCode={projectCode}
      shorthand={shorthand}
      commentId={commentId}
      parentCommentId={parentCommentId}
      onCommentIdConsumed={stripCommentId}
    />
  )
}
